import { DownloadSimpleIcon } from '@phosphor-icons/react';
import { useState } from 'react';
import { toast } from 'react-hot-toast';
import * as Yup from 'yup';
import { Formik } from 'components/form/formik';
import { captureException } from '@sentry/react';

import { BaseButton } from '../../../components/button/BaseButton';
import { Button } from '../../../components/button/Button';
import { Dialog } from '../../../components/dialog/Dialog';
import { SelectWithLabel } from '../../../components/select/SelectWithLabel';
import { InputField } from '../../../components/input/InputField';
import { exportSelfBills } from '../models';
import { getDisplayError } from '../../../utils/get-display-error';
import { downloadTextFile } from '../../../utils/download';
import { parseNumberInput } from '../../../utils/number';

const MONTH_NAMES = [
  'Januari',
  'Februari',
  'Maart',
  'April',
  'Mei',
  'Juni',
  'Juli',
  'Augustus',
  'September',
  'Oktober',
  'November',
  'December',
];

function getMonthOptions(): { value: string; label: string; month: number }[] {
  const now = new Date();
  const options: { value: string; label: string; month: number }[] = [];
  for (let i = -3; i <= 1; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() + i, 1);
    const yy = String(d.getFullYear()).slice(-2);
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yearMonth = `${yy}${mm}`;
    const label = `${MONTH_NAMES[d.getMonth()]} ${d.getFullYear()} (AK${mm})`;
    options.push({ value: yearMonth, label, month: d.getMonth() + 1 });
  }
  return options;
}

function getCurrentYearMonth(): string {
  const now = new Date();
  const yy = String(now.getFullYear()).slice(-2);
  const mm = String(now.getMonth() + 1).padStart(2, '0');
  return `${yy}${mm}`;
}

const schema = Yup.object().shape({
  startInvoiceNumber: Yup.string().min(1, 'Vereist').required('Vereist'),
  endInvoiceNumber: Yup.string().min(1, 'Vereist').required('Vereist'),
  financialPeriod: Yup.string().min(1, 'Vereist').required('Vereist'),
});

export const ExportSelfBillsButton: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const monthOptions = getMonthOptions();
  const currentYearMonth = getCurrentYearMonth();

  return (
    <>
      <BaseButton
        color="default"
        iconLeft={<DownloadSimpleIcon className="button-icon" />}
        isLoading={isExporting}
        disabled={isExporting}
        onClick={() => setOpen(true)}
      >
        Exporteer aankopen
      </BaseButton>

      <Dialog isOpen={open} onClose={() => setOpen(false)} title="Exporteer aankopen (self billing)">
        <Formik
          initialValues={{
            yearMonth: currentYearMonth,
            startInvoiceNumber: `TDA-${currentYearMonth}00001`,
            endInvoiceNumber: `TDA-${currentYearMonth}99999`,
            financialPeriod: String(new Date().getMonth() + 1),
          }}
          validationSchema={schema}
          onSubmit={async ({ startInvoiceNumber, endInvoiceNumber, financialPeriod }) => {
            setIsExporting(true);
            try {
              const result = await exportSelfBills({
                startInvoiceNumber,
                endInvoiceNumber,
                financialPeriod: parseNumberInput(financialPeriod),
              });

              downloadTextFile(result.suppliers, `suppliers-${startInvoiceNumber}-${endInvoiceNumber}.xml`, 'text/xml');
              downloadTextFile(
                result.selfBills,
                `self-bills-${startInvoiceNumber}-${endInvoiceNumber}.xml`,
                'text/xml',
              );

              toast.success('Aankoop export download gestart');
            } catch (err) {
              captureException(err);
              toast.error('Kon aankopen niet exporteren: ' + getDisplayError(err), {
                id: 'export-self-bills-error',
              });
            } finally {
              setIsExporting(false);
            }
          }}
        >
          {({ handleSubmit, isSubmitting, setFieldValue, values }) => {
            const selectedItem = monthOptions.find((o) => o.value === values.yearMonth) ?? null;
            return (
              <form onSubmit={handleSubmit}>
                <SelectWithLabel
                  labelText="Periode"
                  name="Selecteer een periode..."
                  items={monthOptions}
                  selectedItem={selectedItem}
                  keyName="value"
                  display={(item) => item.label}
                  onSelect={(opt) => {
                    if (!opt) return;
                    setFieldValue('yearMonth', opt.value);
                    setFieldValue('startInvoiceNumber', `TDA-${opt.value}00001`);
                    setFieldValue('endInvoiceNumber', `TDA-${opt.value}99999`);
                    setFieldValue('financialPeriod', String(opt.month));
                  }}
                />
                <InputField labelText="Start factuurnummer" type="text" name="startInvoiceNumber" />
                <InputField labelText="Eind factuurnummer" type="text" name="endInvoiceNumber" />
                <InputField labelText="Financiele periode" type="number" name="financialPeriod" />

                <div className="mt-8">
                  <Button
                    type="submit"
                    color="primary"
                    isDisabled={isSubmitting}
                    isLoading={isSubmitting}
                    iconLeft={<DownloadSimpleIcon className="button-icon" />}
                  >
                    Exporteer aankopen
                  </Button>
                </div>
              </form>
            );
          }}
        </Formik>
      </Dialog>
    </>
  );
};
